<template>
    <div class="new-forum-thread panel">
        <div
            class="panel-body"
            v-show="!isLoading">
            <div class="thread-title">
                <input
                    type="text"
                    placeholder="Thread Title"
                    @keyup.enter="$event.target.blur()"
                    v-model="newThread.title">
            </div>

            <div class="thread-category">
                <DropdownSelect
                    placeholder="Select a category"
                    :options="categoryOptions"
                    v-model="newThread.categoryId">
                </DropdownSelect>
            </div>

            <div class="discussion">
                <div class="posts-column">
                    <ForumPost
                        ref="post"
                        :post="initialPost"
                        :isEditing="true">

                        <template slot="stopEditorButton">
                            <!-- Empty, to hide the "Cancel" button -->
                        </template>

                        <template slot="saveEditorButton">
                            <button
                                class="btn-primary"
                                :disabled="!canCreateThread"
                                :title="creationStatusMessage"
                                @click="createThread()">
                                Create thread
                            </button>
                        </template>
                    </ForumPost>

                    <ForumThreadSummary
                        ref="summary"
                        :isEditing="true"
                        :canSave="false"
                        :termTypes.sync="newThread.termTypes"
                        :pathIds.sync="newThread.pathIds"
                        :assignmentIds.sync="newThread.assignmentIds"
                        :tagNames.sync="newThread.tagNames">
                    </ForumThreadSummary>

                    <hr>
                </div>

                <div class="pre-timeline-spacer flex-full"></div>

                <ForumThreadTimeline
                    :posts="timelinePosts"
                    v-model="timelineValue">
                </ForumThreadTimeline>
            </div>
        </div>

        <div
            class="panel-body"
            v-if="isLoading">
            <Loader></Loader>
        </div>
    </div>
</template>

<script>
    // NewForumThread: allows users to create new threads.

    import toastr from 'toastr';

    import ForumService from '../services/forum';
    import ForumUtils from '../utils/forum';
    import DropdownSelect from './reusable/forum/dropdown-select';
    import ForumPost from './reusable/forum/post';
    import ForumThreadSummary from './reusable/forum/thread-summary';
    import ForumThreadTimeline from './reusable/forum/thread-timeline';
    import Loader from './reusable/forum/loader';

    export default {
        components: {
            DropdownSelect,
            ForumPost,
            ForumThreadSummary,
            ForumThreadTimeline,
            Loader,
        },

        data() {
            return {
                initPromise: null,
                loading: true,
                creating: false,
                categories: [],
                timelineValue: 0,
                newThread: {
                    title: '',
                    categoryId: null,
                    content: '',
                    termTypes: [],
                    pathIds: [],
                    assignmentIds: [],
                    tagNames: [],
                },
            };
        },

        created() {
            this.initPromise = (async () => {
                this.loading = true;
                const loadCurrentUser = async () => {
                    await this.$store.dispatch('ensureCurrentUser');
                };
                const loadCategories = async () => {
                    this.categories = await ForumService.getCategories();
                };
                await Promise.all([
                    loadCurrentUser(),
                    loadCategories(),
                ]);
                this.loading = false;
            })();
        },

        mounted() {
            // Load category from query string
            if (this.$route.query.category) {
                const categoryStr = this.$route.query.category;
                this.newThread.categoryId = parseInt(categoryStr) || null;
            }

            // Open thread summary by default
            if (this.$refs.summary) {
                this.$refs.summary.open();
            }
        },

        methods: {
            async createThread() {
                if (this.loading || this.creating) {
                    return;
                }
                try {
                    this.creating = true;

                    // Clear paths if no non-foundation terms are selected.
                    const hasNonFoundation = this.newThread.termTypes.find(tt => {
                        return tt !== 'foundation';
                    });
                    if (!hasNonFoundation) {
                        this.newThread.pathIds = [];
                    }

                    // Upload media
                    const content = await this.uploadContentFiles(
                        this.$refs.post.getContent(),
                        this.$refs.post.getContentFiles(),
                    );

                    // Make sure post has content
                    if (content.trim().length === 0) {
                        toastr.error(`
                            Your post is empty!
                            Try adding some content before posting.
                        `);
                        return;
                    }

                    // Create thread
                    const thread = await ForumService.createThread({
                        title:         this.newThread.title,
                        categoryId:    this.newThread.categoryId,
                        content,
                        termTypes:     this.newThread.termTypes,
                        pathIds:       this.newThread.pathIds,
                        assignmentIds: this.newThread.assignmentIds,
                        tagNames:      this.newThread.tagNames,
                    });

                    // Go to new thread
                    this.$router.push({
                        name: 'forum-thread',
                        params: {
                            threadId: thread.id,
                        },
                    });

                } catch(err) {
                    console.log('error while creating thread:', err);
                    toastr.error('Error while creating thread.');
                } finally {
                    this.creating = false;
                }
            },

            async uploadContentFiles(content, contentFiles) {
                try {
                    return ForumUtils.uploadContentFiles(content, contentFiles);
                } catch (err) {
                    console.log(
                        'Error uploading content files:',
                        err,
                        content,
                        contentFiles,
                    );
                    toastr.error(`
                        There was an error while uploading the post's files.
                        Please try again later.
                    `);
                    throw err;
                }
            },
        },

        computed: {
            cachedUserRoles() {
                return JSON.parse(localStorage.roles);
            },

            currentUser() {
                return this.$store.getters.currentUser;
            },

            currentAuthor() {
                if (!this.currentUser) {
                    return null;
                }
                const fName = this.currentUser.firstName;
                const lName = this.currentUser.lastName;
                return {
                    id: this.currentUser.id,
                    name: `${fName} ${lName}`,
                    roles: this.cachedUserRoles,
                    avatarUrl: this.currentUser.avatar,
                };
            },

            initialPost() {
                return {
                    content: this.newThread.content,
                    author: this.currentAuthor,
                };
            },

            isLoading() {
                return this.loading;
            },

            canCreateThread() {
                return (this.newThread.title.trim() &&
                        this.newThread.categoryId &&
                        !this.creating);
            },

            creationStatusMessage() {
                if (this.creating) {
                    return 'Thread is being created...';
                }
                if (this.canCreateThread) {
                    return 'Thread is ready to post';
                }
                if (!this.newThread.title.trim()) {
                    return 'Thread requires a title';
                }
                if (!this.newThread.categoryId) {
                    return 'Thread requires a category';
                }
                // TODO: handle error
            },

            categoryOptions() {
                return this.categories.map(cat => {
                    return {
                        value: cat.id,
                        label: cat.label,
                    };
                });
            },

            timelinePosts() {
                return [];
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex-full {
        flex: 1;
    }

    button:disabled {
        // Important used only because this is a page, and not a component
        color: #666 !important;
        cursor: not-allowed;
    }

    .panel .panel-body {
        margin: 20px 0;
        padding: 40px;
    }

    .thread-title {
        margin: 0 0 10px;

        input {
            background: #1e1e1e;
            border: 1px solid #484848;
            color: #c0c0c0;
            font-size: 28px;
            height: 45px;
            line-height: 45px;
            padding: 0 10px;
            width: 100%;
        }
    }

    .thread-category {
        margin: 0 0 30px;
        display: flex;
    }

    .discussion {
        display: flex;
        position: relative;

        .posts-column {
            width: 100%;
            max-width: 780px;
            overflow: hidden;

            .forum-loader {
                border-top: 1px solid #333;
            }

            .forum-post {
                border-top: 1px solid #333;
                margin: 0;
                padding-bottom: 20px;
            }

            .forum-thread-summary {
                margin-left: 62px;
                margin-bottom: 20px;
            }

            hr {
                border-top: 1px solid #333;
            }
        }

        .pre-timeline-spacer {
            max-width: 40px;
        }

        .forum-thread-timeline {
            margin-left: 40px;
            margin-bottom: 20px;
        }
    }
</style>

<style lang="scss">
    .new-forum-thread {
        .dropdown-select {
            .dropdown {
                z-index: 101; // must overlap html editor
            }
        }
    }
</style>



// WEBPACK FOOTER //
// NewForumThread.vue?00bf1b4e